// TON wallet (TonConnect) — loaded only when it is needed.
//
// It used to be mounted at the app root: every launch downloaded ~1 MB of wallet
// code and made ~20 requests for wallet icons (config.ton.org), though most
// players never open a wallet. Now the library and @ton/core are imported on the
// first connect / payment — or quietly after start-up when this device already
// has a wallet session, so a connected player stays connected.
import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import type { TonConnectUI } from '@tonconnect/ui';
import { saveWalletMarketplaceSaveWalletPost } from '../client';

// Mainnet USDT jetton master
const USDT_MASTER_ADDRESS = 'EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs';

// Jetton transfer opcode
const JETTON_TRANSFER_OP = 0xf8a7ea5;

// Gas for jetton transfer (~0.05 TON)
const JETTON_TRANSFER_GAS = '0.05';

/** Where TonConnect keeps a wallet session (set only once a wallet was connected here). */
const TON_SESSION_KEY = 'ton-connect-storage_bridge-connection';

const useTonWalletStore = create<{ address: string | null }>(() => ({ address: null }));

let tonUi: Promise<TonConnectUI> | null = null;

/** The TonConnect UI, created on first use. */
function loadTonUi(): Promise<TonConnectUI> {
  if (!tonUi) {
    tonUi = import('@tonconnect/ui').then(({ TonConnectUI }) => {
      const ui = new TonConnectUI({ manifestUrl: `${window.location.origin}/tonconnect-manifest.json` });
      const sync = () => useTonWalletStore.setState({ address: ui.account?.address ?? null });
      ui.onStatusChange(sync);
      void ui.connectionRestored.then(sync);
      return ui;
    });
    tonUi.catch(() => { tonUi = null; });
  }
  return tonUi;
}

const tonCore = () => import('@ton/core');

function hasTonSession(): boolean {
  try { return !!localStorage.getItem(TON_SESSION_KEY); } catch { return false; }
}

/**
 * Resolve the sender's USDT jetton wallet address via TonCenter API.
 */
async function resolveJettonWallet(ownerAddress: string): Promise<string> {
  const { Address, beginCell, Cell } = await tonCore();
  const ownerCell = beginCell().storeAddress(Address.parse(ownerAddress)).endCell();
  const bocBase64 = ownerCell.toBoc().toString('base64');

  // Зовнішній блокчейн-API (toncenter.com), поза нашим OpenAPI — згенерований клієнт не застосовується.
  const resp = await fetch('https://toncenter.com/api/v2/runGetMethod', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      address: Address.parse(USDT_MASTER_ADDRESS).toString(),
      method: 'get_wallet_address',
      stack: [['tvm.Slice', bocBase64]],
    }),
  });

  const data = await resp.json();
  if (!data.ok || !data.result?.stack?.[0]) {
    throw new Error('Failed to resolve jetton wallet address');
  }

  const [, cellB64] = data.result.stack[0];
  const cell = Cell.fromBoc(Buffer.from(cellB64 as string, 'base64'))[0];
  const addr = cell.beginParse().loadAddress();
  return addr.toString();
}

/**
 * Build the body Cell for a jetton (USDT) transfer.
 * USDT has 6 decimals: 1 USDT = 1_000_000.
 */
async function buildJettonTransferBody(params: {
  toAddress: string;
  jettonAmount: bigint;
  senderAddress: string;
  comment: string;
}) {
  const { Address, beginCell } = await tonCore();
  const commentPayload = beginCell()
    .storeUint(0, 32)
    .storeStringTail(params.comment)
    .endCell();

  return beginCell()
    .storeUint(JETTON_TRANSFER_OP, 32)
    .storeUint(0, 64)
    .storeCoins(params.jettonAmount)
    .storeAddress(Address.parse(params.toAddress))
    .storeAddress(Address.parse(params.senderAddress))
    .storeUint(0, 1)
    .storeCoins(1n)
    .storeBit(1)
    .storeRef(commentPayload)
    .endCell();
}

export function useTonConnect() {
  const walletAddress = useTonWalletStore((s) => s.address);
  const savedWalletRef = useRef<string | null>(null);
  const connected = !!walletAddress;

  // A wallet was connected on this device before: bring the library up in the
  // background (after start-up) so the session is restored.
  useEffect(() => {
    if (!hasTonSession()) return;
    const id = window.setTimeout(() => { void loadTonUi().catch(() => {}); }, 3000);
    return () => window.clearTimeout(id);
  }, []);

  // Persist wallet address to backend on TonConnect
  useEffect(() => {
    if (walletAddress && walletAddress !== savedWalletRef.current) {
      savedWalletRef.current = walletAddress;
      saveWalletMarketplaceSaveWalletPost({
        body: { wallet_address: walletAddress },
        throwOnError: true,
      }).catch(() => {});
    }
  }, [walletAddress]);

  /** Send native TON with a comment (MEMO). */
  const sendTon = async (params: {
    to: string;
    amount: string;
    comment: string;
  }) => {
    const [ui, { beginCell, toNano }] = await Promise.all([loadTonUi(), tonCore()]);
    const commentPayload = beginCell()
      .storeUint(0, 32)
      .storeStringTail(params.comment)
      .endCell();

    await ui.sendTransaction({
      validUntil: Math.floor(Date.now() / 1000) + 300,
      messages: [{
        address: params.to,
        amount: toNano(params.amount).toString(),
        payload: commentPayload.toBoc().toString('base64'),
      }],
    });
  };

  /** Send USDT jetton transfer. Resolves jetton wallet automatically. */
  const sendUsdt = async (params: {
    to: string;
    usdtAmount: number;
    comment: string;
  }) => {
    if (!walletAddress) throw new Error('Wallet not connected');

    const senderJettonWallet = await resolveJettonWallet(walletAddress);
    const jettonAmount = BigInt(Math.round(params.usdtAmount * 1_000_000));
    const body = await buildJettonTransferBody({
      toAddress: params.to,
      jettonAmount,
      senderAddress: walletAddress,
      comment: params.comment,
    });

    const [ui, { toNano }] = await Promise.all([loadTonUi(), tonCore()]);
    await ui.sendTransaction({
      validUntil: Math.floor(Date.now() / 1000) + 300,
      messages: [{
        address: senderJettonWallet,
        amount: toNano(JETTON_TRANSFER_GAS).toString(),
        payload: body.toBoc().toString('base64'),
      }],
    });
  };

  const connectWallet = async () => { await (await loadTonUi()).openModal(); };
  const disconnectWallet = async () => { await (await loadTonUi()).disconnect(); };

  return {
    connected,
    walletAddress,
    sendTon,
    sendUsdt,
    connectWallet,
    disconnectWallet,
  };
}
